import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Divider,
  Alert,
  Skeleton,
  IconButton,
  Autocomplete,
  InputAdornment,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { contractsApi, customersApi } from '../../services/api';

const CONTRACT_TYPES = [
  { value: 'domestic', label: 'Domestico' },
  { value: 'commercial', label: 'Commerciale' },
  { value: 'industrial', label: 'Industriale' },
  { value: 'agricultural', label: 'Agricolo' },
  { value: 'resale', label: 'Rivendita' },
];

const INSTALLATION_CODES = [
  { value: '880', label: '880 - Uso domestico esente', vat: 0 },
  { value: '680', label: '680 - Uso commerciale esente', vat: 0 },
  { value: '280', label: '280 - Uso industriale esente', vat: 0 },
  { value: '870', label: '870 - Uso domestico 1%', vat: 1 },
  { value: '670', label: '670 - Uso commerciale 1%', vat: 1 },
  { value: '270', label: '270 - Uso industriale 1%', vat: 1 },
  { value: '800', label: '800 - Uso domestico 22%', vat: 22 },
  { value: '600', label: '600 - Uso commerciale 22%', vat: 22 },
  { value: '200', label: '200 - Uso industriale 22%', vat: 22 },
];

const ContractForm = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEdit = Boolean(id);
  const preselectedCustomerId = searchParams.get('customer');

  const [customerSearch, setCustomerSearch] = useState('');

  const { control, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm({
    defaultValues: {
      customer_id: preselectedCustomerId || '',
      contract_number: '',
      contract_type: 'domestic',
      installation_code: '800',
      start_date: new Date(),
      end_date: null,
      installation_address: '',
      installation_city: '',
      installation_postal_code: '',
      installation_province: '',
      price_per_unit: '',
      annual_fee: '',
      deposit_amount: '',
      notes: '',
      is_active: true,
    },
  });

  // Query clienti per autocomplete
  const { data: customersData } = useQuery({
    queryKey: ['customersSearch', customerSearch],
    queryFn: () => customersApi.getAll({ search: customerSearch, limit: 20 }).then(res => res.data),
    enabled: customerSearch.length > 1 || !!preselectedCustomerId
  });

  // Query contratto esistente
  const { data: contract, isLoading } = useQuery({
    queryKey: ['contract', id],
    queryFn: () => contractsApi.getById(id).then(res => res.data),
    enabled: isEdit,
  });

  // Effetto per resettare il form quando il contratto viene caricato
  React.useEffect(() => {
    if (contract) {
      reset({
        ...contract,
        start_date: contract.start_date ? new Date(contract.start_date) : null,
        end_date: contract.end_date ? new Date(contract.end_date) : null,
      });
    }
  }, [contract, reset]);

  // Mutation salva
  const saveMutation = useMutation({
    mutationFn: (data) => isEdit 
      ? contractsApi.update(id, data)
      : contractsApi.create(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      navigate(isEdit ? `/contratti/${id}` : `/contratti/${response.data.id}`);
    },
  });

  const onSubmit = (data) => {
    const formattedData = {
      ...data,
      start_date: data.start_date?.toISOString().split('T')[0],
      end_date: data.end_date?.toISOString().split('T')[0] || null,
      price_per_unit: parseFloat(data.price_per_unit) || 0,
      annual_fee: parseFloat(data.annual_fee) || 0,
      deposit_amount: parseFloat(data.deposit_amount) || 0,
    };
    saveMutation.mutate(formattedData);
  };

  if (isEdit && isLoading) {
    return (
      <Box>
        <Skeleton height={60} sx={{ mb: 2 }} />
        <Card><CardContent><Skeleton height={400} /></CardContent></Card>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 0, sm: 0 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 1 : 2, mb: isMobile ? 2 : 3, flexWrap: 'wrap' }}>
        <IconButton onClick={() => navigate(isEdit ? `/contratti/${id}` : '/contratti')}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant={isMobile ? 'h6' : 'h4'} fontWeight="bold">
          {isEdit ? 'Modifica Contratto' : 'Nuovo Contratto'}
        </Typography>
      </Box>

      <form onSubmit={handleSubmit(onSubmit)}>
        {saveMutation.isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Errore: {saveMutation.error?.response?.data?.message || 'Errore sconosciuto'}
          </Alert>
        )}

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" gutterBottom>Cliente e Tipo Contratto</Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Controller
                  name="customer_id"
                  control={control}
                  rules={{ required: 'Seleziona un cliente' }}
                  render={({ field }) => (
                    <Autocomplete
                      options={customersData?.customers || []}
                      getOptionLabel={(opt) => opt.business_name || `${opt.last_name} ${opt.first_name}` || ''}
                      onInputChange={(_, value) => setCustomerSearch(value)}
                      onChange={(_, value) => field.onChange(value?.id || '')}
                      value={customersData?.customers?.find(c => c.id === field.value) || null}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Cliente"
                          required
                          error={!!errors.customer_id}
                          helperText={errors.customer_id?.message}
                        />
                      )}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Controller
                  name="contract_number"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} label="Numero Contratto" fullWidth />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Controller
                  name="contract_type"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Tipo Contratto</InputLabel>
                      <Select {...field} label="Tipo Contratto">
                        {CONTRACT_TYPES.map(t => (
                          <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Controller
                  name="installation_code"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Codice Installazione</InputLabel>
                      <Select {...field} label="Codice Installazione">
                        {INSTALLATION_CODES.map(c => (
                          <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Controller
                  name="start_date"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      label="Data Inizio"
                      value={field.value}
                      onChange={field.onChange}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Controller
                  name="end_date"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      label="Data Fine"
                      value={field.value}
                      onChange={field.onChange}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" gutterBottom>Indirizzo Installazione</Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Controller
                  name="installation_address"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} label="Indirizzo" fullWidth />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <Controller
                  name="installation_postal_code"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} label="CAP" fullWidth />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="installation_city"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} label="Località" fullWidth />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <Controller
                  name="installation_province"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} label="Provincia" fullWidth inputProps={{ maxLength: 2 }} />
                  )}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" gutterBottom>Condizioni Economiche</Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Controller
                  name="price_per_unit"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Prezzo al Litro"
                      type="number"
                      fullWidth
                      InputProps={{
                        startAdornment: <InputAdornment position="start">€</InputAdornment>,
                        inputProps: { step: '0.001', min: 0 }
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Controller
                  name="annual_fee"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Canone Annuale"
                      type="number"
                      fullWidth
                      InputProps={{
                        startAdornment: <InputAdornment position="start">€</InputAdornment>,
                        inputProps: { step: '0.01', min: 0 }
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Controller
                  name="deposit_amount"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Deposito Cauzionale"
                      type="number"
                      fullWidth
                      InputProps={{
                        startAdornment: <InputAdornment position="start">€</InputAdornment>,
                        inputProps: { step: '0.01', min: 0 }
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="is_active"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Switch {...field} checked={field.value} />}
                      label="Contratto Attivo"
                    />
                  )}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" gutterBottom>Note</Typography>
            <Divider sx={{ mb: 2 }} />
            <Controller
              name="notes"
              control={control}
              render={({ field }) => (
                <TextField {...field} label="Note" fullWidth multiline rows={3} />
              )}
            />
          </CardContent>
        </Card>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button variant="outlined" onClick={() => navigate('/crm/contratti')}>Annulla</Button>
          <Button type="submit" variant="contained" startIcon={<SaveIcon />} disabled={saveMutation.isLoading}>
            {saveMutation.isLoading ? 'Salvataggio...' : 'Salva'}
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default ContractForm;
